/* ==========================================================================
   ROADNEST — Product media gallery
   Custom element: <product-gallery>
   - one media list for every breakpoint (no duplicated DOM, no duplicate IDs)
   - native horizontal swipe on touch via CSS scroll-snap
   - thumbnails / arrows / dots drive the same scroll container
   - keyboard accessible tablist, variant-media aware, Theme Editor safe
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && window.customElements.get('product-gallery')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function ProductGalleryFactory() {
    return class ProductGallery extends HTMLElement {
      connectedCallback() {
        // Instance flag, not an attribute: a serialised/cloned node must still
        // initialise (the Theme Editor re-injects section markup wholesale).
        if (this.bound) return;
        this.bound = true;

        this.controller = new AbortController();
        var signal = this.controller.signal;

        this.track = this.querySelector('[data-gallery-track]');
        this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-slide]'));
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-thumb]'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-gallery-dot]'));
        this.prevBtn = this.querySelector('[data-gallery-prev]');
        this.nextBtn = this.querySelector('[data-gallery-next]');
        this.counter = this.querySelector('[data-gallery-counter]');
        this.expandBtn = this.querySelector('[data-gallery-expand]');

        if (!this.track || !this.slides.length) return;

        this.setAttribute('data-initialised', '');

        this.index = 0;
        this.ticking = false;
        // While a programmatic scroll is in flight the intermediate scroll
        // positions must not overwrite the target state.
        this.pendingIndex = null;
        this.pendingLeft = 0;

        this.thumbs.forEach(function (thumb, i) {
          thumb.addEventListener('click', function () { this.goTo(i); }.bind(this), { signal: signal });
          thumb.addEventListener('keydown', this.onThumbKey.bind(this, i), { signal: signal });
        }, this);

        this.dots.forEach(function (dot, i) {
          dot.addEventListener('click', function () { this.goTo(i); }.bind(this), { signal: signal });
        }, this);

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', function () { this.goTo(this.index - 1); }.bind(this), { signal: signal });
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', function () { this.goTo(this.index + 1); }.bind(this), { signal: signal });
        }
        if (this.expandBtn) {
          this.expandBtn.addEventListener('click', this.openZoom.bind(this), { signal: signal });
        }

        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true, signal: signal });
        this.track.addEventListener('keydown', this.onTrackKey.bind(this), { signal: signal });

        // A real gesture always wins over an in-flight programmatic scroll.
        var cancelPending = this.clearPending.bind(this);
        ['pointerdown', 'touchstart', 'wheel'].forEach(function (evt) {
          this.track.addEventListener(evt, cancelPending, { passive: true, signal: signal });
        }, this);

        this.slides.forEach(function (slide) {
          var img = slide.querySelector('img');
          if (img) {
            img.addEventListener('click', this.openZoom.bind(this), { signal: signal });
          }
        }, this);

        this.onResize = this.onResize.bind(this);
        window.addEventListener('resize', this.onResize, { passive: true, signal: signal });

        // Jump straight to the media of the pre-selected variant, if any.
        var initial = this.getAttribute('data-initial-media');
        if (initial) this.selectMedia(initial, true);

        this.sync(0);
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        clearTimeout(this.pendingTimer);
        clearTimeout(this.resizeTimer);
        this.bound = false;
      }

      clearPending() {
        this.pendingIndex = null;
        clearTimeout(this.pendingTimer);
      }

      /* ---------- navigation ---------- */

      goTo(i, instant) {
        var count = this.slides.length;
        if (!count) return;
        var next = Math.max(0, Math.min(count - 1, i));
        var slide = this.slides[next];
        if (!slide) return;
        var behavior = instant || reduceMotion.matches ? 'auto' : 'smooth';

        this.pendingIndex = next;
        this.pendingLeft = slide.offsetLeft;
        clearTimeout(this.pendingTimer);
        this.pendingTimer = setTimeout(this.clearPending.bind(this), 900);

        try {
          this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior });
        } catch (err) {
          this.track.scrollLeft = slide.offsetLeft;
        }
        this.sync(next);
      }

      /* Public: used by the variant picker. mediaId may be a Shopify media id. */
      selectMedia(mediaId, instant) {
        if (!mediaId) return;
        var target = -1;
        this.slides.forEach(function (slide, i) {
          if (slide.getAttribute('data-media-id') === String(mediaId)) target = i;
        });
        if (target > -1 && target !== this.index) this.goTo(target, instant);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        window.requestAnimationFrame(function () {
          this.ticking = false;

          if (this.pendingIndex !== null) {
            // Only accept the scroll position once it has reached the target.
            if (Math.abs(this.track.scrollLeft - this.pendingLeft) <= 2) this.clearPending();
            return;
          }

          var mid = this.track.scrollLeft + this.track.clientWidth / 2;
          var found = 0;
          for (var i = 0; i < this.slides.length; i++) {
            var s = this.slides[i];
            if (mid >= s.offsetLeft && mid < s.offsetLeft + s.offsetWidth) { found = i; break; }
            if (mid >= s.offsetLeft) found = i;
          }
          if (found !== this.index) this.sync(found);
        }.bind(this));
      }

      onResize() {
        // Keep the active slide aligned when the layout changes.
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(function () {
          var slide = this.slides[this.index];
          if (!slide) return;
          this.clearPending();
          this.track.scrollLeft = slide.offsetLeft;
        }.bind(this), 120);
      }

      onThumbKey(i, event) {
        var horizontal = window.matchMedia('(max-width: 1023px)').matches;
        var nextKey = horizontal ? 'ArrowRight' : 'ArrowDown';
        var prevKey = horizontal ? 'ArrowLeft' : 'ArrowUp';
        var target = null;
        if (event.key === nextKey || (horizontal && event.key === 'ArrowDown')) target = i + 1;
        else if (event.key === prevKey || (horizontal && event.key === 'ArrowUp')) target = i - 1;
        else if (event.key === 'Home') target = 0;
        else if (event.key === 'End') target = this.slides.length - 1;
        if (target === null) return;
        event.preventDefault();
        target = Math.max(0, Math.min(this.slides.length - 1, target));
        this.goTo(target);
        if (this.thumbs[target]) this.thumbs[target].focus();
      }

      onTrackKey(event) {
        if (event.key === 'ArrowRight') { event.preventDefault(); this.goTo(this.index + 1); }
        else if (event.key === 'ArrowLeft') { event.preventDefault(); this.goTo(this.index - 1); }
      }

      /* ---------- state ---------- */

      sync(index) {
        this.index = index;

        this.thumbs.forEach(function (thumb, i) {
          var active = i === index;
          thumb.setAttribute('aria-selected', active ? 'true' : 'false');
          thumb.setAttribute('tabindex', active ? '0' : '-1');
        });

        this.dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });

        this.slides.forEach(function (slide, i) {
          var hidden = i !== index;
          slide.setAttribute('aria-hidden', hidden ? 'true' : 'false');
          var video = slide.querySelector('video');
          if (video && hidden && !video.paused) video.pause();
          // Promote the neighbouring image so swiping never shows an empty frame.
          if (Math.abs(i - index) <= 1) {
            var img = slide.querySelector('img[loading="lazy"]');
            if (img) img.setAttribute('loading', 'eager');
          }
        });

        this.revealThumb(index);

        if (this.counter) {
          this.counter.textContent = (index + 1) + ' / ' + this.slides.length;
        }
        if (this.prevBtn) this.prevBtn.disabled = index === 0;
        if (this.nextBtn) this.nextBtn.disabled = index === this.slides.length - 1;
      }

      /* Keep the active thumbnail inside the scrollable rail. */
      revealThumb(index) {
        var thumb = this.thumbs[index];
        if (!thumb) return;
        var rail = thumb.parentElement;
        if (!rail) return;

        if (rail.scrollHeight > rail.clientHeight + 1) {
          var top = thumb.offsetTop;
          var bottom = top + thumb.offsetHeight;
          if (top < rail.scrollTop) rail.scrollTop = top;
          else if (bottom > rail.scrollTop + rail.clientHeight) rail.scrollTop = bottom - rail.clientHeight;
        }
        if (rail.scrollWidth > rail.clientWidth + 1) {
          var left = thumb.offsetLeft;
          var right = left + thumb.offsetWidth;
          if (left < rail.scrollLeft) rail.scrollLeft = left;
          else if (right > rail.scrollLeft + rail.clientWidth) rail.scrollLeft = right - rail.clientWidth;
        }
      }

      /* ---------- zoom ---------- */

      openZoom() {
        var slide = this.slides[this.index];
        if (!slide) return;
        var img = slide.querySelector('img');
        if (!img) return;
        var full = img.getAttribute('data-zoom-src') || img.currentSrc || img.src;
        if (window.Roadnest && typeof window.Roadnest.openLightbox === 'function') {
          window.Roadnest.openLightbox(full, img.alt || '');
        }
      }
    };
  }

  window.customElements.define('product-gallery', ProductGalleryFactory());
})();
